import React, { useEffect, useState } from "react";
import { inject, observer } from "mobx-react";
import {
  fn_getComportListView,
  fn_setComportListView,
  fn_getSelectedComport,
  fn_setSelectedComport,
  fn_setHookingFlag,
  fn_getHookingFlag,
  fn_setKitchenPrinterFlag,
  fn_getKitchenPrinterFlag,
  fn_setSelectedKitchen,
  fn_setTestPrint,
  fn_setKitchenPrinterType,
  fn_setPayFreeType,
  fn_setExceptPrintType,
  fn_getSelectedBaudrate,
  fn_setDefaultSelectedComport,
  fn_getDefaultSelectedComport,
} from "function/FnBodySetting";
import { Switcher } from "components/ui/Switcher";
import { SectionTitle } from "components/ui/SectionTitle";
import InfoPanel from "components/ui/InfoPanel";

type YesOrNo = "Y" | "N";
type PrinterType = "hooking" | "virtual" | "none";
type ComPort = {
  portNo: string;
  portName: string;
};

type kitchenPrinterSettings = {
  printerCode: string;
  printerName: any;
  posNo: string;
  comPort: string;
  baudRate: string;
};

// 프린터 타입 숫자 대신 문자열로 치환 (가독성)
const getPrinterType = (flag: string): PrinterType => {
  /**
   * 0 : 기본(후킹)
   * 1 : 가상프린터
   * 2 : 사용안함
   **/
  if (flag === "0") {
    return "hooking";
  }
  if (flag === "1") {
    return "virtual";
  }
  return "none";
};

// 연결포트 리스트 타입 추가 반환
const getComPortList = (comPortsList: ComPort[]) => {
  return (
    comPortsList.map(port => ({
      portNo: port.portNo,
      portName: port.portName,
    })) ?? []
  );
};

// 주방동시출력 프린터 설정 타입 추가 반환
const getKitchenPrinterSettings = (list: kitchenPrinterSettings[]) => {
  return (
    list?.map(setting => ({
      printerCode: setting.printerCode,
      printerName: setting.printerName,
      baudRate: setting.baudRate,
      posNo: setting.posNo,
      comPort: setting.comPort,
    })) ?? []
  );
};

// 라디오 선택 컴포넌트
const RadioSelector = ({
  className,
  name,
  checked,
  onChange,
  type,
  title,
  subTitle,
}: {
  className: string;
  name: "print" | "type";
  checked: boolean;
  type: "shop" | "customer";
  title: string;
  subTitle: string;
  onChange: () => void;
}) => {
  return (
    <div className={className}>
      <label>
        <input type="radio" name={name === "print" ? "printType" : name} checked={checked} onChange={onChange} />
        <p className={type}>
          <span className={`tit ${name === "print" ? "print" : ""}`}>{title}</span>
          <span className="txt">{subTitle}</span>
        </p>
      </label>
    </div>
  );
};

const SettingDefaultPrinterSetView = ({ rootStore }: { rootStore: any }) => {
  const { settingStore, searchStore } = rootStore;

  const virtualModeFlag = settingStore.getPrinterInfo.usedVirtualModeFlag;
  const printerType = getPrinterType(virtualModeFlag);

  // 주문접수 프린터 설정 여부 (Y : 사용 / N : 미사용)
  const IsPrinterUse = settingStore.getPrinterInfo.hookingFlag as YesOrNo;

  // 주문동시출력 주문서 양식 > 주문 접수 시 (0 : 매장용 / 1 : 고객용)
  const kitchenPrinterType = settingStore.getPrinterInfo.kitchenPrinterType as "0" | "1";

  // 주방동시출력 설정 여부 (Y : 사용 / N : 미사용)
  const kitchenPrinterFlag = settingStore.getPrinterInfo.kitchenPrinterFlag as YesOrNo;

  // 주방동시출력 프린터 설정
  const kitchenPrinterSettingsList = getKitchenPrinterSettings(settingStore.getPrinterInfo.kitchenPrinterSettings);

  // hooking - 연결포트 리스트
  const comPortsList = getComPortList(settingStore.getPrinterInfo.availableComPorts);

  // hooking - 연결포트 리스트 오픈 상태
  const [isComportListOpen, setIsComportListOpen] = useState<boolean>(false);

  // virtual - 출력 방식 여부 (Y : 연동 출력물 설정 / N : 주방동시출력)
  const virtualMode = settingStore.getPrinterInfo.usedVirtualModeExceptPrint as YesOrNo;

  // 주방동시출력 노출 조건
  const useKitchenPrinterSetting = (printerType === "hooking" ||
    (printerType === "virtual" && virtualMode === "N")) as boolean;

  useEffect(() => {
    settingStore.setPrinterInfo(settingStore.getPrinterInfo);
    settingStore.setSettingUseMenu("printer_setting");
    settingStore.setSettingView(false);
  }, []);

  // 가상 프린터 사용 안함
  if (printerType === "none") {
    return (
      <div className="scroll tab">
        <div className="inner">
          <div className="wrap_print">
            <SectionTitle title="주문접수 프린터 설정" subTitle="배달앱 주문을 인식하는 프린터 설정" addBottomBorder />
            <div className="list_none print">
              <h5 className="tit">
                <span className="print">주문 인식 기능 미사용 매장입니다</span>
              </h5>
              <p className="print">관리자에 문의하세요 (1544-3583)</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="scroll tab">
      <div className="inner">
        <div className="wrap_print">
          <SectionTitle addBottomBorder title="주문접수 프린터 설정" subTitle="배달앱 주문을 인식하는 프린터 설정">
            <Switcher
              size="lg"
              checked={IsPrinterUse === "Y"}
              onChange={() => fn_setHookingFlag(rootStore, !fn_getHookingFlag(rootStore))}
              showLabel={true}
              switcherColor="primary"
              onLabel="사용"
              offLabel="미사용"
            />
          </SectionTitle>
          {IsPrinterUse === "N" ? (
            <InfoPanel
              variant="smile"
              title="배달앱 주문서 인식을 위해 프린터를 설정하세요"
              subtitle="배민/쿠팡이츠/요기요 등 배달앱에서 출력하는 주문서를 인식하여 주문을 생성해줍니다"
              className="min-h-[250px]"
            />
          ) : (
            <>
              {/* 후킹 프린터 설정 */}
              {printerType === "hooking" && (
                <div>
                  <div className={"box_port"}>
                    <strong className="tit">연결포트</strong>
                    <div className="select">
                      <button type="button" onClick={() => setIsComportListOpen(!isComportListOpen)}>
                        <span>{fn_getDefaultSelectedComport(rootStore)}</span>
                      </button>
                      <ul className={"layer " + (isComportListOpen ? "open" : "")}>
                        {(comPortsList ?? []).map(port => (
                          <li key={port.portNo}>
                            <button
                              type="button"
                              onClick={event => {
                                event.preventDefault();
                                fn_setDefaultSelectedComport(rootStore, port.portNo);
                                setIsComportListOpen(false);
                              }}
                            >
                              {port.portName}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className={"box_info"}>
                    <ul className="list">
                      <li>배달앱 주문서가 출력되는 포트(PORT)로 설정해주세요</li>
                      <li>프린터 연결이 되었는지 테스트 출력을 통해 확인하세요</li>
                      <button type="submit" className="btn_print" onClick={() => fn_setTestPrint(rootStore, "P")}>
                        <span className="ico_right">테스트 출력</span>
                      </button>
                    </ul>
                  </div>
                </div>
              )}

              {/* 가상 프린터 설정 */}
              {printerType === "virtual" && (
                <>
                  <div className={"box_cont print"}>
                    <ul className="list_print print">
                      <li className="print">
                        <div className="tit">출력 방식</div>
                        <div className="btn print">
                          <RadioSelector
                            className="btn_s"
                            name="print"
                            checked={virtualMode === "N"}
                            onChange={() => fn_setExceptPrintType(rootStore, "N")}
                            title="주방동시출력"
                            subTitle="(전표 그대로 출력)"
                            type="shop"
                          />
                          <RadioSelector
                            className="btn_c"
                            name="print"
                            checked={virtualMode === "Y"}
                            onChange={() => fn_setExceptPrintType(rootStore, "Y")}
                            title="연동 출력물 설정"
                            subTitle="(배매 양식으로 출력)"
                            type="customer"
                          />
                        </div>
                      </li>
                      <li className="print">
                        <div className="tit">가상 포트</div>
                        <div className="box_port port l">
                          <div className="select port">
                            <button
                              type="button"
                              onClick={() =>
                                fn_setComportListView(rootStore, "port1", !fn_getComportListView(rootStore, "port1"))
                              }
                            >
                              <span>{fn_getSelectedComport(rootStore, "port1")}</span>
                            </button>
                            <ul className={"layer " + (fn_getComportListView(rootStore, "port1") ? "open" : "")}>
                              {(comPortsList ?? []).map(port => (
                                <li className="portList" key={port.portNo}>
                                  <button
                                    type="button"
                                    onClick={event => {
                                      event.preventDefault();
                                      fn_setSelectedComport(rootStore, port.portNo, "port1");
                                    }}
                                  >
                                    {port.portName}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="box_port port">
                          <div className="select port">
                            <button
                              type="button"
                              onClick={() =>
                                fn_setComportListView(rootStore, "port2", !fn_getComportListView(rootStore, "port2"))
                              }
                            >
                              <span>{fn_getSelectedComport(rootStore, "port2")}</span>
                            </button>
                            <ul className={"layer " + (fn_getComportListView(rootStore, "port2") ? "open" : "")}>
                              {(comPortsList ?? []).map(port => (
                                <li className="portList" key={port.portNo}>
                                  <button
                                    type="button"
                                    onClick={event => {
                                      event.preventDefault();
                                      fn_setSelectedComport(rootStore, port.portNo, "port2");
                                    }}
                                  >
                                    {port.portName}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </li>
                      <li className="print">
                        <div className="tit">출력 포트</div>
                        <div className="box_port port l">
                          <div className="select port">
                            <button
                              type="button"
                              onClick={() =>
                                fn_setComportListView(
                                  rootStore,
                                  "printPort",
                                  !fn_getComportListView(rootStore, "printPort")
                                )
                              }
                            >
                              <span>{fn_getSelectedComport(rootStore, "printPort")}</span>
                            </button>
                            <ul className={"layer " + (fn_getComportListView(rootStore, "printPort") ? "open" : "")}>
                              {(comPortsList ?? []).map((port, index) => (
                                <li className="portList" key={index}>
                                  <button
                                    type="button"
                                    onClick={event => {
                                      event.preventDefault();
                                      fn_setSelectedComport(rootStore, port.portNo, "printPort");
                                    }}
                                  >
                                    {port.portName}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="box_port port">
                          <div className="select port">
                            <button
                              type="button"
                              onClick={() =>
                                fn_setComportListView(
                                  rootStore,
                                  "baudrate",
                                  !fn_getComportListView(rootStore, "baudrate")
                                )
                              }
                            >
                              <span>{fn_getSelectedBaudrate(rootStore)}</span>
                            </button>
                            <ul className={"layer " + (fn_getComportListView(rootStore, "baudrate") ? "open" : "")}>
                              {(searchStore.getPrintAddSelectBaudRate ?? []).map((val: string, index: number) => (
                                <li className="portList" key={index}>
                                  <button
                                    type="button"
                                    onClick={event => {
                                      event.preventDefault();
                                      fn_setSelectedComport(rootStore, val, "baudrate");
                                    }}
                                  >
                                    {val}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                  {comPortsList.length > 0 && IsPrinterUse === "Y" && (
                    <div className={"box_info print"}>
                      <p>설정 및 변경 사항은 포스 재실행 후 적용됩니다</p>
                    </div>
                  )}
                </>
              )}

              {/* 주방동시출력 설정 노출 */}
              {useKitchenPrinterSetting &&
                (settingStore.getMainPosType === "Y" ? (
                  <div>
                    <SectionTitle
                      addBottomBorder
                      title="주방동시출력 설정"
                      subTitle="인식한 주문서 그대로 출력하는 프린터 설정"
                    >
                      <Switcher
                        size="lg"
                        checked={kitchenPrinterFlag === "Y"}
                        onChange={() => {
                          settingStore.getPayFreeType.paymentUseYn === "1"
                            ? fn_setPayFreeType(rootStore)
                            : fn_setKitchenPrinterFlag(rootStore, !fn_getKitchenPrinterFlag(rootStore));
                        }}
                        showLabel={true}
                        onLabel="사용"
                        offLabel="미사용"
                        switcherColor="primary"
                      />
                    </SectionTitle>

                    {/* 주방동시출력 미사용 */}
                    {kitchenPrinterFlag === "N" && (
                      <InfoPanel
                        variant="smile"
                        title="배달앱 주문서를 주방에도 동시에 출력해보세요!"
                        subtitle="배민/쿠팡이츠/요기요 등 배달앱 전표양식 그대로 선택한 프린터에 출력해줍니다."
                        className="min-h-[200px]"
                      />
                    )}

                    {/* 주방동시출력 설정 없음 */}
                    {kitchenPrinterFlag === "Y" && kitchenPrinterSettingsList?.length < 1 && (
                      <InfoPanel variant="smile" title="설정된 주방프린터가 없습니다." className="min-h-[200px]" />
                    )}

                    {kitchenPrinterSettingsList?.length > 0 && kitchenPrinterFlag === "Y" && (
                      <div className="box_cont">
                        <h4>주방동시출력 주문서 양식</h4>
                        <span className="tit">주방동시출력 시 주문서 양식 설정</span>
                        <ul className="list_print">
                          <li>
                            <div className="tit">주문 접수시</div>
                            <div className="btn">
                              <RadioSelector
                                className="btn_s"
                                name="type"
                                checked={!kitchenPrinterType || kitchenPrinterType === "0"}
                                onChange={() => fn_setKitchenPrinterType(rootStore, "0")}
                                title="매장용"
                                subTitle="(주문서)"
                                type="shop"
                              />
                              <RadioSelector
                                className="btn_c"
                                name="type"
                                checked={kitchenPrinterType === "1"}
                                onChange={() => fn_setKitchenPrinterType(rootStore, "1")}
                                title="고객용"
                                subTitle="(주문전표)"
                                type="customer"
                              />
                            </div>
                          </li>
                          <li>
                            <div className="tit">재출력시</div>
                            <div className="box">
                              <p>고객용만 출력 가능</p>
                            </div>
                          </li>
                        </ul>
                      </div>
                    )}

                    {/* 주방동시출력 프린터 설정 */}
                    {kitchenPrinterSettingsList?.length > 0 && kitchenPrinterFlag === "Y" && (
                      <>
                        <div className={"box_check"}>
                          <h4>주방동시출력 프린터 설정</h4>
                          <span className="tit">주방동시출력 프린터 선택(다수 가능)</span>
                          <ul className="list_checkbox">
                            {kitchenPrinterSettingsList.map((info, index) => (
                              <li key={index}>
                                <label>
                                  <input
                                    type="checkbox"
                                    value={info.printerCode}
                                    onChange={event =>
                                      fn_setSelectedKitchen(rootStore, info.printerCode, event.target.checked)
                                    }
                                    checked={settingStore.getPrinterInfo.usedKitchenPrinterCodes.includes(
                                      info.printerCode
                                    )}
                                  />
                                  <span className="ico"></span>
                                  <span className="inner">
                                    <span className="clear_g">
                                      <span className="f_l">포스번호 : {info.posNo}</span>
                                      <span className="f_r">{info.comPort}</span>
                                    </span>
                                    <span className="clear_g">
                                      <strong className="f_l">
                                        {info.printerName?.length > 0 ? info.printerName : "프린터명없음"}
                                      </strong>
                                      <span className="f_r">{info.baudRate}</span>
                                    </span>
                                  </span>
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className={"box_info bottom"}>
                          <ul className="list">
                            <li>배달앱 주문서를 그대로 동시 출력할 프린터를 선택하세요</li>
                            <li>프린터 연결이 되었는지 테스트 출력을 통해 확인하세요</li>
                            <button type="button" className="btn_print" onClick={() => fn_setTestPrint(rootStore, "K")}>
                              <span className="ico_right">테스트 출력</span>
                            </button>
                          </ul>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  // 서브포스 설정
                  <>
                    <SectionTitle
                      addBottomBorder
                      title="주방동시출력 설정"
                      subTitle="인식한 주문서 그대로 출력하는 프린터 설정"
                    />
                    <InfoPanel
                      variant="smile"
                      title="메인포스에서 설정해 주세요"
                      subtitle={`현재 사용중인 포스는 ${searchStore.getSession.connPosNo}번 포스입니다`}
                      className="min-h-[200px]"
                    />
                  </>
                ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default inject("rootStore")(observer(SettingDefaultPrinterSetView));
